import { createBrowserRouter, Navigate } from "react-router";

import App from "../App";
import Login from "../routes/login";
import LogoutPage from "../routes/logout";
import ForgotPasswordPage from "../routes/forgot-password";
import Signup from "../routes/signup";
import VerifyMagicLink from "../routes/verify-magic-link";
import Dashboard from "../routes/dashboard";
import Users from "../routes/dashboard/users";
import DashboardHome from "../routes/dashboard/home";
import UserDetailsPage from "../routes/dashboard/personal-cabinet";
import Customers from "../routes/dashboard/customers";
import Invoices from "../routes/dashboard/invoices";
import LienWaivers from "../routes/dashboard/vendors";
import VendorAudit from "../routes/dashboard/vendor-audit";
import ConfirmEmail from "../routes/confirm-email";
import ResetPassword from "../routes/reset-password";
import NotFound from "../routes/not-found";
import PaymentForm from "../routes/payment-form";
import DropboxFolders from "../routes/dashboard/dropbox-folders";
import AccountSettings from "./dashboard/account-settings";
import Integrations from "./dashboard/integrations";
import IESOAuthCallback from "./ies-oauth-callback";
import DocumentLibrary from "../routes/dashboard/document-library";
import DocumentLibraryAdmin from "../routes/dashboard/document-library-admin";
import WhatToExpect from "../routes/dashboard/what-to-expect";
import ClientOverview from "../routes/dashboard/client-overview";
import ErrorBoundary from "../components/ErrorBoundary";
import RequireRole from "../components/RequireRole";
import RequirePermission from "../components/RequirePermission";

const router = createBrowserRouter([
  {
    Component: App,
    errorElement: <NotFound />,
    children: [
      {
        path: "/",
        element: <Navigate to="/login" replace />,
      },
      {
        // Intuit redirects the browser here after consent — the app is tied to
        // this domain — and the page forwards the parameters to the API, which
        // exchanges the code. Public on purpose: the one-time state protects it.
        path: "/ies/oauth/callback",
        Component: IESOAuthCallback,
      },
      {
        path: "/dashboard",
        Component: Dashboard,
        children: [
          {
            index: true,
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <DashboardHome />
              </RequireRole>
            ),
          },
          {
            path: "document-library",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <ErrorBoundary fallbackMessage="Unable to load Document Library">
                  <DocumentLibraryAdmin />
                </ErrorBoundary>
              </RequireRole>
            ),
          },
          {
            path: "users",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <Users />
              </RequireRole>
            ),
          },
          {
            path: "customers",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <Customers />
              </RequireRole>
            ),
          },
          {
            path: "lien-waivers",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <LienWaivers />
              </RequireRole>
            ),
          },
          {
            path: "invoices",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <Invoices />
              </RequireRole>
            ),
          },
          {
            path: "dropbox-folders",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <DropboxFolders />
              </RequireRole>
            ),
          },
          {
            path: "vendor-audit",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <VendorAudit />
              </RequireRole>
            ),
          },
          {
            path: "client-overview",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <ErrorBoundary fallbackMessage="Unable to load Client Overview">
                  <ClientOverview />
                </ErrorBoundary>
              </RequireRole>
            ),
          },
          {
            path: "account-settings",
            Component: AccountSettings,
          },
          {
            path: "integrations",
            element: (
              <RequireRole roles={["admin", "super-admin"]}>
                <RequirePermission permission="integrations">
                  <Integrations />
                </RequirePermission>
              </RequireRole>
            ),
          },
        ],
      },
      {
        path: "/project-home",
        Component: Dashboard,
        children: [
          {
            path: "financial",
            element: (
              <RequireRole roles={["client", "employee"]}>
                <ErrorBoundary fallbackMessage="Unable to load Financial Dashboard">
                  <UserDetailsPage />
                </ErrorBoundary>
              </RequireRole>
            ),
          },
          {
            path: "documents",
            element: (
              <RequireRole roles={["client", "employee"]}>
                <ErrorBoundary fallbackMessage="Unable to load Documents">
                  <DocumentLibrary />
                </ErrorBoundary>
              </RequireRole>
            ),
          },
          {
            path: "meet-your-team",
            element: (
              <RequireRole roles={["client", "employee"]}>
                <ErrorBoundary fallbackMessage="Unable to load What to Expect">
                  <WhatToExpect />
                </ErrorBoundary>
              </RequireRole>
            ),
          },
        ],
      },
      {
        path: "/login",
        Component: Login,
      },
      {
        path: "/logout",
        Component: LogoutPage,
      },
      {
        path: "/forgot-password",
        Component: ForgotPasswordPage,
      },
      {
        path: "/signup",
        Component: Signup,
      },
      {
        path: "/verify-secure-link/:token",
        Component: VerifyMagicLink,
      },
      {
        path: "/confirm-email",
        Component: ConfirmEmail,
      },
      { path: "/reset-password", Component: ResetPassword },
      { path: "/payment-form", Component: PaymentForm },
      {
        path: "/not-found",
        Component: NotFound,
      },
    ],
  },
]);

export { router };
